import {
    useState,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IColorSliderThumbProps from "./type";
import type {
    ColorSliderThumbAccessibilityActionEventType,
    ColorSliderThumbKeyboardEventType,
    ColorSliderThumbWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    getInteractionStateName,
    useInteractionState
} from "../../../../helpers/interaction";

const ColorSliderThumb: FC<IColorSliderThumbProps> = ({
    "aria-describedby": ariaDescribedBy,
    onKeyDown: onKeyDownProp,
    "aria-label": ariaLabel,
    onKeyUp: onKeyUpProp,
    currentMeasures,
    currentColors,
    customTheme,
    orientation,
    pointerType,
    isDisabled,
    fillColor,
    isPressed,
    valueText,
    valueMax,
    valueMin,
    valueNow,
    viewRef,
    offset,
    onStep
}) => {
    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const [
        isHovered,
        setIsHovered
    ] = useState<boolean>(false);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDisabled
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        currentMeasures,
        isFocusVisible,
        currentColors,
        shapeConfigs,
        orientation,
        pointerType,
        shadowAlpha,
        elevations,
        isDisabled,
        fillColor,
        isHovered,
        isPressed,
        radiuses,
        borders,
        colors,
        offset
    });

    const onKeyDown = (event: ColorSliderThumbKeyboardEventType) => {
        onKeyDownProp?.(event);
    };

    const onKeyUp = (event: ColorSliderThumbKeyboardEventType) => {
        onKeyUpProp?.(event);
    };

    const onPointerEnter = () => {
        setIsHovered(true);
    };

    const onPointerLeave = () => {
        setIsHovered(false);
    };

    const onAccessibilityAction = ({
        nativeEvent
    }: ColorSliderThumbAccessibilityActionEventType) => {
        if(nativeEvent.actionName === "increment") {
            onStep?.(1);

            return;
        }

        if(nativeEvent.actionName === "decrement") {
            onStep?.(-1);
        }
    };

    const webProps: ColorSliderThumbWebPropsType = Platform.OS === "web" ? {
        dataSet: {
            [INTERACTION_STATE_DATA_KEY]: getInteractionStateName({
                isHovered: isHovered && pointerType === "fine",
                isFocusVisible,
                isDisabled,
                isPressed
            })
        },
        "aria-orientation": orientation,
        onKeyDown,
        onKeyUp
    } : {};

    return <View
        {...webProps}
        onAccessibilityAction={onAccessibilityAction}
        style={[
            stylesheet.container,
            isPressed ? stylesheet.pressed : null,
            isDisabled ? stylesheet.disabled : null,
            containerDynamicStyle
        ]}
        onFocus={interactionHandlers.onFocus}
        onBlur={interactionHandlers.onBlur}
        aria-describedby={ariaDescribedBy}
        onPointerLeave={onPointerLeave}
        onPointerEnter={onPointerEnter}
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        aria-valuetext={valueText}
        accessibilityActions={[
            {
                name: "increment"
            },
            {
                name: "decrement"
            }
        ]}
        aria-valuemin={valueMin}
        aria-valuemax={valueMax}
        aria-valuenow={valueNow}
        focusable={!isDisabled}
        aria-label={ariaLabel}
        accessible={true}
        role="slider"
        ref={viewRef}
    />;
};
export default ColorSliderThumb;
